import { useSetAtom, useAtomValue } from 'jotai';
import { useEffect, useState, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import DailyIframe, { type DailyCall } from '@daily-co/daily-js';
import { DailyProvider } from '@daily-co/daily-react';
import { PhoneOff } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import {
  StartCallScreen,
  EndCallScreen,
  CallInner,
} from '@/components/Conversation';
import {
  conversationRoomUrlAtom,
  conversationSourceNameAtom,
  conversationLoadingAtom,
  conversationErrorAtom,
  fetchConversationAtom,
  stopConversationAtom,
  conversationIdAtom,
} from '@/stores/dailyCallStore';

export function Conversation() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const [callObject, setCallObject] = useState<DailyCall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<'start' | 'in-call' | 'end'>('start');
  // To avoid refetching on same ID
  const fetchedRef = useRef<string | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const needsRecording = searchParams.get('record') === 'true';

  const roomUrl = useAtomValue(conversationRoomUrlAtom);
  const sourceName = useAtomValue(conversationSourceNameAtom);
  const conversationId = useAtomValue(conversationIdAtom);
  const isFetching = useAtomValue(conversationLoadingAtom);
  const convError = useAtomValue(conversationErrorAtom);

  const fetchConversation = useSetAtom(fetchConversationAtom);
  const stopCall = useSetAtom(stopConversationAtom);

  useEffect(() => {
    if (stage !== 'in-call' || !roomUrl) return;

    let cancelled = false;

    const setup = async () => {
      const existing = DailyIframe.getCallInstance();
      if (existing) {
        try {
          await existing.destroy();
        } catch {}
        if (cancelled) return;
      }
      const call = DailyIframe.createCallObject();
      setCallObject(call);
    };

    void setup();

    return () => {
      cancelled = true;
      const inst = DailyIframe.getCallInstance();
      if (inst) void inst.destroy();
    };
  }, [roomUrl, stage]);

  const handleFinish = async () => {
    try {
      await callObject?.leave();
    } catch {}
    setStage('end');

    if (!conversationId) return;
    await stopCall(conversationId);
  };

  useEffect(() => {
    if (!id) return;

    if (fetchedRef.current === id) return;

    fetchedRef.current = id;
    void fetchConversation({ id, needsRecording });
  }, [id, needsRecording, fetchConversation]);

  return (
    <div className='mx-auto flex w-full max-w-5xl flex-col gap-4 p-4'>
      {sourceName && (
        <header className='flex items-center justify-between'>
          <h1 className='text-lg font-semibold tracking-tight'>
            Interview with {sourceName}
          </h1>
        </header>
      )}

      {convError && (
        <div className='rounded-md border border-destructive bg-destructive/10 p-3'>
          <p className='text-sm text-destructive'>{convError}</p>
        </div>
      )}

      {stage === 'start' && (
        <section className='w-full'>
          <AspectRatio ratio={16 / 9}>
            <StartCallScreen
              onStart={() => setStage('in-call')}
              disabled={isFetching || !roomUrl}
              loading={isFetching}
            />
          </AspectRatio>
        </section>
      )}

      {stage === 'in-call' && callObject && (
        <>
          <section className='w-full'>
            <AspectRatio ratio={16 / 9}>
              <DailyProvider callObject={callObject}>
                <CallInner
                  url={roomUrl as string}
                  showLocal={false}
                  onError={setError}
                  onEnd={() => setStage('end')}
                  needsRecording={needsRecording}
                />
              </DailyProvider>
            </AspectRatio>
            {error && <p className='mt-2 text-sm text-destructive'>{error}</p>}
          </section>

          <footer className='mt-2 flex items-center justify-center gap-3'>
            <Button variant='destructive' size='lg' onClick={handleFinish}>
              <PhoneOff className='size-4' />
              Finish Call
            </Button>
          </footer>
        </>
      )}

      {stage === 'end' && (
        <section className='w-full'>
          <AspectRatio ratio={16 / 9}>
            <EndCallScreen />
          </AspectRatio>
        </section>
      )}
    </div>
  );
}
